import { useEffect, useState } from "react";
import { useMarketFreshness } from "../context/LiveStreamContext";
import { Clock, Zap } from "lucide-react";

/**
 * Shows a subtle status pill telling the user whether prices are truly live
 * or being served from a cache because upstream providers (yfinance / CoinGecko)
 * are throttling us. Rerenders every 10s so the "delayed" state appears
 * automatically when the pipeline stalls, without needing a page refresh.
 *
 * Props:
 *   symbols        — optional array of {symbol} or plain strings to scope the check
 *   staleSeconds   — threshold above which we call the data "delayed" (default 90s)
 *   testId         — data-testid on the outer element
 */
export function StalenessBadge({ symbols, staleSeconds = 90, testId = "staleness-badge" }) {
  const [, tick] = useState(0);
  // Re-render every 10s so the age computation stays fresh even when no new
  // WS tick arrives (the whole point of this badge).
  useEffect(() => {
    const iv = setInterval(() => tick((x) => x + 1), 10000);
    return () => clearInterval(iv);
  }, []);
  const { fresh, oldest_seconds } = useMarketFreshness(symbols, staleSeconds);

  if (!oldest_seconds) return null;  // no tick data yet — don't spam a status
  if (fresh) {
    return (
      <span data-testid={testId}
            data-fresh="true"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono-tab uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <Zap className="w-3 h-3" /> live · {oldest_seconds}s
      </span>
    );
  }
  const mins = Math.round(oldest_seconds / 60);
  return (
    <span data-testid={testId}
          data-fresh="false"
          title="Upstream provider is throttling us — prices are being served from cache. Set CG_DEMO_API_KEY on the backend to raise CoinGecko limits."
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono-tab uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
      <Clock className="w-3 h-3" /> delayed · {mins < 2 ? `${oldest_seconds}s` : `${mins}m`}
    </span>
  );
}
